import {
  type DragEvent as ReactDragEvent,
  type DragEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

export interface UseDropzoneOptions {
  /**
   * Called when one or more files are dropped.
   * Omit to disable the dropzone.
   */
  onFilesDropped?: (files: FileList) => unknown

  /**
   * Called when a file drag first enters the dropzone.
   */
  onEnter?: () => void

  /**
   * Called when a file drag leaves or is dropped on the dropzone.
   */
  onLeave?: () => void
}

export interface DropzoneProps<T extends HTMLElement = HTMLElement> {
  onDragEnter: DragEventHandler<T>
  onDragLeave: DragEventHandler<T>
  onDragOver: DragEventHandler<T>
  onDrop: DragEventHandler<T>
}

export interface UseDropzoneResult<T extends HTMLElement = HTMLElement> {
  isDragging: boolean
  isOver: boolean
  dropzoneProps: DropzoneProps<T>
}

/**
 * Provides the state and React event handlers needed for a file dropzone.
 */
export function useDropzone<T extends HTMLElement = HTMLElement>({
  onFilesDropped,
  onEnter,
  onLeave,
}: UseDropzoneOptions): UseDropzoneResult<T> {
  const isEnabled = Boolean(onFilesDropped)
  const [isDragging, setIsDragging] = useState(false)
  const [isOver, setIsOver] = useState(false)

  // Drag events fire as the pointer moves between descendants. Track their
  // depth so the dropzone only leaves when the outermost drag has left.
  const dragDepth = useRef(0)

  useEffect(() => {
    let windowDragDepth = 0

    function onWindowDragEnter(event: DragEvent) {
      if (windowDragDepth === 0 && !isFileDrag(event)) {
        return
      }

      windowDragDepth += 1
      if (windowDragDepth === 1) {
        setIsDragging(true)
      }
    }

    function onWindowDragLeave() {
      if (windowDragDepth === 0) {
        return
      }

      windowDragDepth -= 1
      if (windowDragDepth === 0) {
        setIsDragging(false)
      }
    }

    function resetWindowDragState() {
      windowDragDepth = 0
      setIsDragging(false)
    }

    window.addEventListener('dragenter', onWindowDragEnter)
    window.addEventListener('dragleave', onWindowDragLeave)
    window.addEventListener('drop', resetWindowDragState)
    window.addEventListener('dragend', resetWindowDragState)

    return () => {
      window.removeEventListener('dragenter', onWindowDragEnter)
      window.removeEventListener('dragleave', onWindowDragLeave)
      window.removeEventListener('drop', resetWindowDragState)
      window.removeEventListener('dragend', resetWindowDragState)
    }
  }, [])

  const resetDragState = useCallback(() => {
    const wasOver = dragDepth.current > 0
    dragDepth.current = 0
    setIsOver(false)

    if (wasOver && isEnabled) {
      onLeave?.()
    }
  }, [isEnabled, onLeave])

  const onDragEnter: DragEventHandler<T> = useCallback(
    (event) => {
      if (dragDepth.current === 0 && !isFileDrag(event)) {
        return
      }

      dragDepth.current += 1

      if (dragDepth.current === 1) {
        setIsOver(true)
        if (isEnabled) {
          onEnter?.()
        }
      }
    },
    [isEnabled, onEnter],
  )

  const onDragLeave: DragEventHandler<T> = useCallback(() => {
    if (dragDepth.current === 0) {
      return
    }

    dragDepth.current -= 1

    if (dragDepth.current === 0) {
      setIsOver(false)
      if (isEnabled) {
        onLeave?.()
      }
    }
  }, [isEnabled, onLeave])

  const onDragOver: DragEventHandler<T> = useCallback(
    (event) => {
      if (!onFilesDropped || (dragDepth.current === 0 && !isFileDrag(event))) {
        return
      }

      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    [onFilesDropped],
  )

  const onDrop: DragEventHandler<T> = useCallback(
    (event) => {
      if (event.dataTransfer.files.length === 0) {
        return
      }

      const files = event.dataTransfer.files
      resetDragState()

      if (!onFilesDropped) {
        return
      }

      event.preventDefault()
      onFilesDropped(files)
    },
    [onFilesDropped, resetDragState],
  )

  return {
    isDragging: isEnabled && isDragging,
    isOver: isEnabled && isOver,
    dropzoneProps: {
      onDragEnter,
      onDragLeave,
      onDragOver,
      onDrop,
    },
  }
}

function isFileDrag(event: ReactDragEvent<HTMLElement> | DragEvent): boolean {
  return event.dataTransfer?.types.includes('Files') ?? false
}
